// Models
import { ContentType } from "../client/planner/common/IItemBody";
// Constants
import { Allowed_CSS_Properties } from "@ms/uno-constants/lib/local/DOMPurifyConstants";
/**
 * CSS properties Roosterjs emits for bulleted-list / flex layout that the Planner service
 * rejects. The client DOMPurify allowlist permits these (they live in Allowed_CSS_Properties),
 * but the server's Ganss.XSS.HtmlSanitizer strips them. When stripping leaves an empty style
 * attribute, the server throws InvalidHtmlException ([StyleAttributeEmpty]) and the whole
 * PATCH fails with HTTP 400. We strip them here so the serialized notes never carry these
 * server-rejected properties. See incident 21000000969718, bugs #8848167 / #9034548.
 */ // The `(^|;)` boundary anchors the match to the start of a declaration (start of the style
// value or immediately after a ";"), so a property name appearing inside another declaration's
// value — e.g. `content:'align-self:start'` — is left untouched. The leading boundary is
// consumed along with the declaration, which removes its separating ";" too.
const SERVER_REJECTED_CSS_PROPS = /(?:^|;)\s*(?:align-content|align-items|align-self|flex|flex-basis|flex-direction|flex-flow|flex-grow|flex-shrink|flex-wrap|justify-content)\s*:[^;]*/gi;
// `display` is allowlisted on the server for most values, but `display:flex` specifically is
// rejected, so strip only that value (keeps display:block, display:none, inline-flex, etc.).
// `(?![\w-])` ensures we match the value `flex` exactly, not a prefix like `flex-foo`.
const FLEX_DISPLAY = /(?:^|;)\s*display\s*:\s*flex(?![\w-])[^;]*/gi;
// Matches a single- or double-quoted style attribute, capturing the leading whitespace and the
// quote char so we can drop the whole attribute (including its space) when it becomes empty.
const STYLE_ATTRIBUTE = /(\s)style=(["'])([\s\S]*?)\2/gi;
const CSS_DECLARATION = /(?:^|;)\s*([\w-]+)\s*:\s*([^;]*)/g;
// Approximates what the service's CSS parser keeps. The client allowlist errs wide (it also permits
// the flex/align family the service rejects, stripped above before this runs), so a false positive
// keeps an attribute rather than dropping one the service would accept.
const SERVICE_RETAINED_PROPERTIES = new Set(Allowed_CSS_Properties);
/**
 * Determines whether the service's CSS parser would retain at least one declaration, meaning an
 * allowlisted property with a non-empty value. When none survives the service drops the attribute
 * and fails the whole write with [StyleAttributeEmpty]. The value is only tested, never rebuilt, so
 * declarations containing ";" such as data URIs stay intact.
 * @param styleValue The style attribute value to inspect
 */ function hasServiceRetainedDeclaration(styleValue) {
    for (const [, property, value] of styleValue.matchAll(CSS_DECLARATION)){
        if (value.trim() !== "" && SERVICE_RETAINED_PROPERTIES.has(property.toLowerCase())) {
            return true;
        }
    }
    return false;
}
/**
 * Removes CSS properties from inline `style` attributes that the Planner service rejects,
 * dropping the attribute entirely when nothing valid remains.
 * @param html The notes HTML to clean
 */ function stripServerInvalidStyles(html) {
    return html.replace(STYLE_ATTRIBUTE, (_match, space, quote, styleValue)=>{
        const cleaned = styleValue.replace(SERVER_REJECTED_CSS_PROPS, "").replace(FLEX_DISPLAY, "")// Preserve valid runs of up to two semicolons, but cap longer runs at two.
        .replace(/;{3,}/g, ";;").replace(/^\s*;+/, "").trim();
        return hasServiceRetainedDeclaration(cleaned) ? `${space}style=${quote}${cleaned}${quote}` : "";
    });
}
/**
 * Serializes the given notes into a resource format.
 * @param notes The notes to serialize
 */ export function serializeNotes(notes) {
    if (!notes) {
        return null;
    }
    return {
        content: stripServerInvalidStyles(notes.content),
        contentType: ContentType.HTML
    };
}
/**
 * Deserializes the given resource into notes.
 * @param resource The resource to deserialize
 */ export function deserializeNotes(resource) {
    if (!resource) {
        return null;
    }
    return {
        content: resource.content,
        contentType: ContentType.HTML
    };
}
